/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useMemo, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { isArray } from 'lodash';
import {
    Box,
    Typography,
    Chip,
    alpha,
    Collapse,
    IconButton,
    TextField,
    InputAdornment,
    CircularProgress,
    Avatar,
    Autocomplete,
    Checkbox,
    Tooltip,
    Dialog,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    Popover,
    List,
    ListItemButton,
    ListItemText,
    Divider,
    Badge,
    Button,
    Link,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Iconify } from 'src/components';
import { PATH_DASHBOARD } from 'src/routes/paths';
import { useSelector, dispatch as reduxDispatch } from 'src/redux/store';
import {
    getTablePreferences,
    handleTablePreferences,
    handleTablePreferencesEdit,
    setTablePreferenceLoading,
} from 'src/redux/slices/tablePreferences';
import { getCurrentTableData } from 'src/utils/tablePreferences';
import useGetCurrentUserId from 'src/hooks/useGetCurrentUserId';
import useReportExportFilename from 'src/hooks/useReportExportFilename';
import { STATUS_COLORS } from 'src/constants/reportStatusPalette';
import DemoBlur from 'src/components/DemoBlur';
import useDemoMode from 'src/hooks/useDemoMode';
import DataScopeIndicator from './DataScopeIndicator';

// Lead-status label for this popup's Status column and its CSV export.
// Uses the canonical reports palette so a lead reads the same here as in the
// By Treatment / By Source drilldowns; falls back to the underscore-stripped
// enum for any status the palette does not carry.
const statusLabelOf = (status) => STATUS_COLORS[status]?.label || status?.replace(/_/g, ' ') || '-';

const TABLE_NAME = 'Clinician Conversion Tracker';

// Row metrics configuration - matching CRM status labels
const ROW_METRICS = [
    { key: 'consultationsBooked', label: 'Consult Booked', color: '#8b5cf6' },
    { key: 'consultationsAttended', label: 'Attended', color: '#3b82f6' },
    { key: 'treatmentStarted', label: 'Treatment Started', color: '#f59e0b' },
    { key: 'teethCount', label: 'Teeth', color: '#0ea5e9', icon: 'mdi:tooth-outline' },
    { key: 'conversionRate', label: '% Conversion', color: '#10b981', suffix: '%' },
    { key: 'revenue', label: '£ Value', color: '#10b981', prefix: '£', format: 'currency' },
];

// Default treatment columns (partial match - will match if treatment name contains these)
const DEFAULT_COLUMN_KEYWORDS = [
    'Invisalign',
    'Bonding',
    'New Patient',
    'Dental Implant',
    'Emerg',
];

export default function ClinicianConversionTracker({
    title,
    data,
    treatments,
    teamTotals,
    isLoading,
    exportDateRange,
    exportPracticeName,
    practiceScope,
    failedPractices,
    selectedCount,
    coveredCount,
    onRetry,
}) {
    const navigate = useNavigate();
    const buildExportName = useReportExportFilename();
    const [expandedPractitioner, setExpandedPractitioner] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedTreatments, setSelectedTreatments] = useState([]);
    // Optional Practice filter — lives in the toolbar (mirrors the Treatment
    // filter's Autocomplete pattern), defaults to empty = "All selected
    // practices". Only rendered/consulted when `data` spans more than one
    // practice (see `showPracticeColumn` below); a single-practice selection
    // never shows this control, keeping that UX unchanged. Deliberately NOT
    // persisted to table preferences (unlike the Treatment filter) — the
    // practice SET itself already comes from the page-level selection, so
    // this is a transient in-session refinement, not a saved layout choice.
    const [selectedPractices, setSelectedPractices] = useState([]);
    const [selectedMetrics, setSelectedMetrics] = useState(ROW_METRICS.map((m) => m.key));
    const [prefsLoaded, setPrefsLoaded] = useState(false);
    const [sortField, setSortField] = useState(null); // 'name' | 'total' | treatment name
    const [sortDirection, setSortDirection] = useState('desc'); // 'asc' | 'desc'

    // Toolbar popover anchors
    const [columnsAnchor, setColumnsAnchor] = useState(null);
    const [exportAnchor, setExportAnchor] = useState(null);

    // Popup state for showing lead treatment details
    const [popupOpen, setPopupOpen] = useState(false);
    const [popupDetails, setPopupDetails] = useState([]);
    const [popupTitle, setPopupTitle] = useState('');

    // Table preferences from Redux
    const userId = useGetCurrentUserId();
    const { tablePreferences: tablePrefsRaw, globalTablePreferences } = useSelector((state) => state.tablePreferences);
    const prefResults = tablePrefsRaw?.results || tablePrefsRaw || [];

    // Load user preferences on mount
    useEffect(() => {
        if (userId) {
            reduxDispatch(getTablePreferences({ practiceUserId: userId }));
        }
    }, [userId]);

    // Save preferences to API (columns + visible metrics)
    const savePreferences = useCallback((newTreatments, newMetrics) => {
        if (!userId) return;
        reduxDispatch(setTablePreferenceLoading(true));
        const tableData = getCurrentTableData(prefResults, TABLE_NAME);
        const globalData = getCurrentTableData(globalTablePreferences?.results || [], TABLE_NAME);
        const currentTableData = isArray(tableData) && tableData.length > 0 ? tableData : globalData;
        const currentPreferences = currentTableData?.[0]?.preferences || {};
        const updatedPrefs = { ...currentPreferences, columns: newTreatments };
        if (newMetrics) updatedPrefs.visibleMetrics = newMetrics;
        const payload = {
            body: { table_name: TABLE_NAME, preferences: updatedPrefs },
            practiceUserId: userId,
        };
        if (isArray(tableData) && tableData.length > 0) {
            reduxDispatch(handleTablePreferencesEdit({ ...payload, tableId: tableData[0].id }));
        } else {
            reduxDispatch(handleTablePreferences(payload));
        }
    }, [userId, prefResults, globalTablePreferences]);

    // Handle treatment selection change (update state + persist)
    const handleTreatmentChange = useCallback((_, newValue) => {
        setSelectedTreatments(newValue);
        savePreferences(newValue, selectedMetrics);
    }, [savePreferences, selectedMetrics]);

    // Handle metric row visibility toggle
    const handleMetricToggle = useCallback((metricKey) => {
        const updated = selectedMetrics.includes(metricKey)
            ? selectedMetrics.filter((k) => k !== metricKey)
            : [...selectedMetrics, metricKey];
        setSelectedMetrics(updated);
        savePreferences(selectedTreatments, updated);
    }, [selectedMetrics, selectedTreatments, savePreferences]);

    // Move a selected treatment up or down in display order
    const handleMoveTreatment = useCallback((name, direction) => {
        const idx = selectedTreatments.indexOf(name);
        if (idx < 0) return;
        const newIdx = direction === 'up' ? idx - 1 : idx + 1;
        if (newIdx < 0 || newIdx >= selectedTreatments.length) return;
        const updated = [...selectedTreatments];
        [updated[idx], updated[newIdx]] = [updated[newIdx], updated[idx]];
        setSelectedTreatments(updated);
        savePreferences(updated, selectedMetrics);
    }, [selectedTreatments, selectedMetrics, savePreferences]);

    // Move a metric row up or down in display order
    const handleMoveMetric = useCallback((metricKey, direction) => {
        const idx = selectedMetrics.indexOf(metricKey);
        if (idx < 0) return;
        const newIdx = direction === 'up' ? idx - 1 : idx + 1;
        if (newIdx < 0 || newIdx >= selectedMetrics.length) return;
        const updated = [...selectedMetrics];
        [updated[idx], updated[newIdx]] = [updated[newIdx], updated[idx]];
        setSelectedMetrics(updated);
        savePreferences(selectedTreatments, updated);
    }, [selectedMetrics, selectedTreatments, savePreferences]);

    // Toggle sort on a column header: none → desc → asc → none (3-click cycle)
    const handleSort = useCallback((field) => {
        if (sortField === field) {
            if (sortDirection === 'desc') {
                setSortDirection('asc');
            } else {
                // Was asc → clear sort entirely
                setSortField(null);
                setSortDirection('desc');
            }
        } else {
            setSortField(field);
            setSortDirection(field === 'name' ? 'asc' : 'desc');
        }
    }, [sortField, sortDirection]);

    // Every distinct (practiceId, practiceName) pair present in `data` — the
    // thunk tags every unioned row with these when 2+ practices are selected.
    // A single-practice fetch never sets practiceId, so this (and every
    // Practice-column/filter/totals behaviour gated on it) is a no-op then —
    // single-practice UX is byte-identical to before this feature.
    const practiceOptions = useMemo(() => {
        const map = new Map();
        (data || []).forEach((p) => {
            if (p.practiceId != null && !map.has(p.practiceId)) {
                map.set(p.practiceId, p.practiceName || String(p.practiceId));
            }
        });
        return Array.from(map.entries()).map(([id, name]) => ({ id: String(id), name }));
    }, [data]);
    // Row identity = practice + practitioner (never collapse a same-name/
    // same-user practitioner across two different practices into one row).
    const getRowKey = useCallback(
        (p) => (p.practiceId != null ? `${p.practiceId}-${p.id}` : p.id),
        []
    );
    // The Practice column/filter must reflect the page-level SELECTED scope,
    // not merely which practices happened to return rows — otherwise a
    // 2+ practice selection where only one practice responds (others
    // empty/403/error) would silently lose the Practice column and toolbar
    // filter. `practiceScope` (the full selected set) is authoritative when
    // supplied; `practiceOptions` (row-derived) remains a fallback for any
    // caller that doesn't pass a scope (By Practitioner / single-practice —
    // byte-identical to before this prop existed).
    const scopeSize = practiceScope?.length || 0;
    const showPracticeColumn = scopeSize > 1 || practiceOptions.length > 1;
    // Toolbar Practice-filter options: the full selected scope (so a practice
    // that returned zero rows or failed is still a filterable option, and
    // filtering to it shows an honest empty) unioned with any practiceId
    // present in data as a safety net.
    const effectivePracticeOptions = useMemo(() => {
        if (scopeSize === 0) return practiceOptions;
        const map = new Map();
        practiceScope.forEach((p) => map.set(String(p.id), p.name));
        practiceOptions.forEach((p) => { if (!map.has(p.id)) map.set(p.id, p.name); });
        return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
    }, [practiceScope, scopeSize, practiceOptions]);
    // Partial-coverage state — when one or more selected practices failed to
    // load (access denied / hard error), the summary/export/UI must stay
    // truthful about it rather than silently presenting the union as complete.
    const isPartialCoverage = failedPractices.length > 0;
    const effectiveExportPracticeName = isPartialCoverage && exportPracticeName
        ? `${exportPracticeName} (partial)`
        : exportPracticeName;

    // Data scoped by the toolbar Practice filter ONLY (independent of the name
    // search box and column sort) — this is what totals/CSV export follow, so
    // they stay correct regardless of what the user has typed into search or
    // how they've sorted the visible rows.
    const practiceFilteredData = useMemo(() => {
        if (!data) return [];
        if (!showPracticeColumn || selectedPractices.length === 0) return data;
        return data.filter((p) => selectedPractices.includes(String(p.practiceId)));
    }, [data, showPracticeColumn, selectedPractices]);

    // Team totals, re-aggregated client-side ONLY when the toolbar Practice
    // filter has actually narrowed the selection — otherwise pass the
    // redux-computed `teamTotals` straight through unchanged (byte-identical
    // to pre-multi-practice behaviour whenever no practice filter is active).
    const displayTeamTotals = useMemo(() => {
        if (!showPracticeColumn || selectedPractices.length === 0) return teamTotals;
        const t = {
            leads: 0, consultationsBooked: 0, consultationsAttended: 0, treatmentStarted: 0, revenue: 0,
        };
        practiceFilteredData.forEach((p) => {
            t.leads += p.totals?.leads || 0;
            t.consultationsBooked += p.totals?.consultationsBooked || 0;
            t.consultationsAttended += p.totals?.consultationsAttended || 0;
            t.treatmentStarted += p.totals?.treatmentStarted || 0;
            t.revenue += p.totals?.revenue || 0;
        });
        t.showUpRate = t.consultationsBooked > 0
            ? ((t.consultationsAttended / t.consultationsBooked) * 100).toFixed(1) : '0';
        t.conversionRate = t.consultationsAttended > 0
            ? ((t.treatmentStarted / t.consultationsAttended) * 100).toFixed(1) : '0';
        return t;
    }, [teamTotals, showPracticeColumn, selectedPractices, practiceFilteredData]);

    // Filter + sort practitioners
    const filteredPractitioners = useMemo(() => {
        if (!practiceFilteredData) return [];
        let result = practiceFilteredData;
        if (searchQuery) {
            result = result.filter((p) =>
                p.name.toLowerCase().includes(searchQuery.toLowerCase())
            );
        }
        if (sortField) {
            result = [...result].sort((a, b) => {
                // Keep "Unassigned" row at the bottom regardless of sort
                if (a._isUnassigned) return 1;
                if (b._isUnassigned) return -1;
                let aVal;
                let bVal;
                if (sortField === 'name') {
                    aVal = a.name?.toLowerCase() || '';
                    bVal = b.name?.toLowerCase() || '';
                    return sortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
                }
                if (sortField === 'total') {
                    aVal = parseFloat(a.totals?.conversionRate) || 0;
                    bVal = parseFloat(b.totals?.conversionRate) || 0;
                } else {
                    // Sort by a specific treatment's conversion rate
                    const aData = a.treatments?.[sortField] || null;
                    const bData = b.treatments?.[sortField] || null;
                    aVal = parseFloat(aData?.conversionRate) || 0;
                    bVal = parseFloat(bData?.conversionRate) || 0;
                }
                return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
            });
        }
        return result;
    }, [practiceFilteredData, searchQuery, sortField, sortDirection]);

    // All available treatment names
    const allTreatmentNames = useMemo(() => {
        if (!treatments || treatments.length === 0) return [];
        return treatments.map((t) => t.name);
    }, [treatments]);

    // Load saved treatment selection + metric visibility from preferences, or use defaults
    useEffect(() => {
        if (allTreatmentNames.length > 0 && !prefsLoaded) {
            const tableData = getCurrentTableData(prefResults, TABLE_NAME);
            const globalData = getCurrentTableData(globalTablePreferences?.results || [], TABLE_NAME);
            const currentTableData = isArray(tableData) && tableData.length > 0 ? tableData : globalData;
            const prefs = currentTableData?.[0]?.preferences;
            // Restore metric visibility
            const savedMetrics = prefs?.visibleMetrics;
            if (isArray(savedMetrics) && savedMetrics.length > 0) {
                const validMetrics = savedMetrics.filter((k) => ROW_METRICS.some((m) => m.key === k));
                if (validMetrics.length > 0) setSelectedMetrics(validMetrics);
            }
            // Restore column selection
            const savedColumns = prefs?.columns;
            if (isArray(savedColumns) && savedColumns.length > 0) {
                const validSaved = savedColumns.filter((c) => allTreatmentNames.includes(c));
                if (validSaved.length > 0) {
                    setSelectedTreatments(validSaved);
                    setPrefsLoaded(true);
                    return;
                }
            }
            // Fallback to defaults
            const defaults = allTreatmentNames.filter((treatmentName) =>
                DEFAULT_COLUMN_KEYWORDS.some((keyword) =>
                    treatmentName.toLowerCase().includes(keyword.toLowerCase())
                )
            );
            setSelectedTreatments(defaults.length > 0 ? defaults : allTreatmentNames.slice(0, 5));
            setPrefsLoaded(true);
        }
    }, [allTreatmentNames, prefResults, globalTablePreferences]);

    // Filtered row metrics based on user selection, preserving user's order
    const visibleMetrics = useMemo(() =>
        selectedMetrics.map((key) => ROW_METRICS.find((m) => m.key === key)).filter(Boolean)
    , [selectedMetrics]);

    // Build treatment columns from selected treatments
    const treatmentColumns = useMemo(() => {
        if (selectedTreatments.length > 0) {
            return selectedTreatments;
        }
        return allTreatmentNames.slice(0, 5); // Fallback to first 5
    }, [selectedTreatments, allTreatmentNames]);

    // Whether the column/metric filter differs from showing everything
    const isFilterActive = useMemo(() => {
        const treatmentsFiltered = selectedTreatments.length > 0 && selectedTreatments.length < allTreatmentNames.length;
        const metricsFiltered = selectedMetrics.length < ROW_METRICS.length;
        return treatmentsFiltered || metricsFiltered;
    }, [selectedTreatments, allTreatmentNames, selectedMetrics]);

    // Popup/detail titles follow the selected set AND the toolbar Practice
    // filter automatically — a row is only clickable if it's currently
    // rendered, and filtered-out practices' rows never render. When the data
    // spans 2+ practices, append the row's own practiceName so the popup
    // makes the practice attribution explicit.
    const [demoMode] = useDemoMode();
    const buildMetricTitle = (practitioner, suffix) => {
        const who = demoMode ? 'Practitioner' : practitioner.name;
        return showPracticeColumn && practitioner.practiceName
            ? `${who} - ${suffix} • ${practitioner.practiceName}`
            : `${who} - ${suffix}`;
    };

    // Handle click on a metric chip to show lead treatment details
    const handleMetricClick = (e, details, metricTitle) => {
        e.stopPropagation();
        if (details && details.length > 0) {
            setPopupDetails(details);
            setPopupTitle(metricTitle);
            setPopupOpen(true);
        }
    };

    const handleClosePopup = () => {
        setPopupOpen(false);
        setPopupDetails([]);
        setPopupTitle('');
    };

    // Export popup details to CSV
    const handleExportPopupCSV = useCallback(() => {
        if (!popupDetails || popupDetails.length === 0) return;
        const headers = ['#', 'Lead Name', 'Treatment', 'Status', 'Times contacted', 'Date', 'Price', 'Teeth'];
        const csvRows = popupDetails.map((row, idx) => {
            let dateStr = '-';
            if (row.createdAt) {
                try {
                    const parts = row.createdAt.match(/^(\d{2})-(\d{2})-(\d{4})/);
                    if (parts) {
                        dateStr = `${parts[1]}/${parts[2]}/${parts[3]}`;
                    } else {
                        const d = new Date(row.createdAt);
                        if (!Number.isNaN(d.getTime())) dateStr = d.toLocaleDateString('en-GB');
                    }
                } catch { dateStr = '-'; }
            }
            return [
                idx + 1,
                row.leadName || '-',
                row.treatment || '-',
                statusLabelOf(row.status),
                row.timesContacted || 0,
                dateStr,
                row.price > 0 ? `£${row.price.toLocaleString()}` : '-',
                row.teethCount > 0 ? row.teethCount : '-',
            ];
        });
        const csvContent = [headers, ...csvRows].map((row) => row.map((cell) => `"${cell}"`).join(',')).join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = buildExportName('clinician-leads', exportDateRange, { practiceName: exportPracticeName });
        link.click();
        URL.revokeObjectURL(link.href);
    }, [popupDetails, buildExportName, exportDateRange, exportPracticeName]);

    // Export to CSV — follows the selected practice set AND the toolbar
    // Practice filter (practiceFilteredData), not the raw `data` prop.
    const handleExportCSV = useCallback(() => {
        setExportAnchor(null);
        const exportRows = practiceFilteredData;
        if (!exportRows || exportRows.length === 0) return;
        const cols = treatmentColumns;
        // Build header row
        const headers = [
            'Team Member',
            ...(showPracticeColumn ? ['Practice'] : []),
            ...cols,
            'Total Booked', 'Total Attended', 'Total Started', 'Conversion %', 'Revenue',
        ];
        // Build data rows
        const rows = exportRows.map((p) => {
            const treatmentCells = cols.map((t) => {
                const td = p.treatments?.[t] || null;
                return td ? `${td.treatmentStarted}/${td.consultationsBooked}` : '-';
            });
            return [
                p.name,
                ...(showPracticeColumn ? [p.practiceName || ''] : []),
                ...treatmentCells,
                p.totals?.consultationsBooked || 0,
                p.totals?.consultationsAttended || 0,
                p.totals?.treatmentStarted || 0,
                `${p.totals?.conversionRate || 0}%`,
                p.totals?.revenue || 0,
            ];
        });
        const csvBody = [headers, ...rows];
        if (isPartialCoverage) {
            csvBody.unshift([`Partial coverage: ${coveredCount} of ${selectedCount} practices`]);
        }
        const csvContent = csvBody.map((row) => row.map((cell) => `"${cell}"`).join(',')).join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = buildExportName('clinician-conversion-tracker', exportDateRange, { practiceName: effectiveExportPracticeName });
        link.click();
        URL.revokeObjectURL(link.href);
    }, [practiceFilteredData, showPracticeColumn, treatmentColumns, buildExportName, exportDateRange, effectiveExportPracticeName, isPartialCoverage, coveredCount, selectedCount]);

    // Export expanded detail CSV (one row per practitioner per metric per
    // treatment) — same practiceFilteredData scoping as the summary export.
    const handleExportDetailCSV = useCallback(() => {
        setExportAnchor(null);
        const exportRows = practiceFilteredData;
        if (!exportRows || exportRows.length === 0) return;
        const cols = treatmentColumns;
        const headers = ['Team Member', ...(showPracticeColumn ? ['Practice'] : []), 'Metric', ...cols, 'Total'];
        const rows = [];
        exportRows.forEach((p) => {
            visibleMetrics.forEach((metric) => {
                const treatmentCells = cols.map((t) => {
                    const td = p.treatments?.[t] || null;
                    const val = td?.[metric.key] || 0;
                    if (metric.format === 'currency') return val;
                    if (metric.suffix === '%') return `${val}%`;
                    return val;
                });
                rows.push([
                    p.name,
                    ...(showPracticeColumn ? [p.practiceName || ''] : []),
                    metric.label,
                    ...treatmentCells,
                    p.totals?.[metric.key] || 0,
                ]);
            });
        });
        const csvBody = [headers, ...rows];
        if (isPartialCoverage) {
            csvBody.unshift([`Partial coverage: ${coveredCount} of ${selectedCount} practices`]);
        }
        const csvContent = csvBody.map((row) => row.map((cell) => `"${cell}"`).join(',')).join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = buildExportName('clinician-conversion-detail', exportDateRange, { practiceName: effectiveExportPracticeName });
        link.click();
        URL.revokeObjectURL(link.href);
    }, [practiceFilteredData, showPracticeColumn, treatmentColumns, visibleMetrics, buildExportName, exportDateRange, effectiveExportPracticeName, isPartialCoverage, coveredCount, selectedCount]);

    // Consistent grid template for all rows — an extra Practice column is
    // inserted right after Team Member whenever the data spans 2+ practices.
    const gridTemplate = useMemo(() => {
        const treatmentCols = treatmentColumns.length > 0 ? treatmentColumns.length : 1;
        const practiceCol = showPracticeColumn ? 'minmax(120px, 0.7fr) ' : '';
        return `minmax(180px, 1.2fr) ${practiceCol}repeat(${treatmentCols}, minmax(100px, 1fr)) minmax(120px, 1fr)`;
    }, [treatmentColumns, showPracticeColumn]);

    const handleToggleExpand = (practitionerId) => {
        setExpandedPractitioner(
            expandedPractitioner === practitionerId ? null : practitionerId
        );
    };

    // Format currency value
    const formatValue = (value, format, prefix = '', suffix = '') => {
        if (format === 'currency') {
            if (value >= 1000000) return `${prefix}${(value / 1000000).toFixed(1)}M`;
            if (value >= 1000) return `${prefix}${(value / 1000).toFixed(1)}K`;
            return `${prefix}${value}`;
        }
        return `${prefix}${value}${suffix}`;
    };

    // Get color based on conversion rate - matching existing scheme
    const getConversionColor = (rate) => {
        const numRate = parseFloat(rate) || 0;
        if (numRate >= 70) return '#10b981'; // Green
        if (numRate >= 50) return '#f59e0b'; // Amber
        return '#ef4444'; // Red
    };

    if (isLoading) {
        return (
            <Box
                sx={{
                    bgcolor: '#fff',
                    borderRadius: '16px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                    p: 4,
                    mb: 3,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: 300,
                }}
            >
                <CircularProgress />
            </Box>
        );
    }

    if (!data || data.length === 0) {
        // Partial/access failure hiding behind a zero-row union — one or more
        // selected practices returned 401/403 (not a hard slot error, so the
        // caller still renders us, not its own error card) and the union has
        // no rows to show for it. Rendering the generic empty-state here would
        // silently hide the access failure, so show a truthful access card
        // instead — same wording/styling as the partial-coverage banner below.
        if (isPartialCoverage) {
            return (
                <Box
                    sx={{
                        bgcolor: '#fff',
                        borderRadius: '16px',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                        p: 4,
                        mb: 3,
                        textAlign: 'center',
                    }}
                >
                    <Box
                        sx={{
                            width: 64,
                            height: 64,
                            borderRadius: '16px',
                            bgcolor: alpha('#f59e0b', 0.12),
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mx: 'auto',
                            mb: 2,
                        }}
                    >
                        <Iconify icon="mdi:alert-outline" width={32} sx={{ color: '#b45309' }} />
                    </Box>
                    <Typography sx={{ fontSize: '16px', fontWeight: 600, color: '#475569', mb: 1 }}>
                        {`Couldn't load clinician data for ${failedPractices.length} of ${selectedCount} selected practices`}
                    </Typography>
                    <Typography sx={{ fontSize: '14px', color: '#94a3b8', mb: onRetry ? 2 : 0 }}>
                        {failedPractices.map((fp, idx) => (
                            // eslint-disable-next-line react/no-array-index-key
                            <Box component="span" key={`${fp.practiceId}-${idx}`}>
                                {idx > 0 ? ', ' : ''}
                                {`${fp.practiceName} (${fp.reason})`}
                            </Box>
                        ))}
                    </Typography>
                    {onRetry && (
                        <Button variant="outlined" color="warning" onClick={onRetry}>
                            Retry
                        </Button>
                    )}
                </Box>
            );
        }

        return (
            <Box
                sx={{
                    bgcolor: '#fff',
                    borderRadius: '16px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                    p: 4,
                    mb: 3,
                    textAlign: 'center',
                }}
            >
                <Box
                    sx={{
                        width: 64,
                        height: 64,
                        borderRadius: '16px',
                        bgcolor: '#f1f5f9',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mx: 'auto',
                        mb: 2,
                    }}
                >
                    <Iconify icon="mdi:account-group-outline" width={32} sx={{ color: '#94a3b8' }} />
                </Box>
                <Typography sx={{ fontSize: '16px', fontWeight: 600, color: '#475569', mb: 1 }}>
                    No Conversion Data Available
                </Typography>
                <Typography sx={{ fontSize: '14px', color: '#94a3b8' }}>
                    Conversion tracking data will appear here once team members have consultations and treatments recorded.
                </Typography>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                bgcolor: '#fff',
                borderRadius: '16px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                overflow: 'hidden',
                mb: 3,
            }}
        >
            {/* Header - matching existing table headers */}
            <Box
                sx={{
                    p: 2.5,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 2,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Tooltip title="Tracks how each clinician converts consultations to treatments by treatment type." placement="bottom" arrow
                        slotProps={{ tooltip: { sx: { bgcolor: '#1e293b', fontSize: '12px', maxWidth: 280 } } }}>
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, cursor: 'help' }}>
                            <Typography sx={{ fontSize: '20px', fontWeight: 700 }}>
                                {title}
                            </Typography>
                            <Iconify icon="mdi:information-outline" width={14} sx={{ color: '#94a3b8' }} />
                        </Box>
                    </Tooltip>
                    {displayTeamTotals && displayTeamTotals.treatmentStarted > 0 && (
                        <Chip
                            label={`${displayTeamTotals.treatmentStarted} Treatments`}
                            size="small"
                            sx={{
                                bgcolor: alpha('#10b981', 0.12),
                                color: '#10b981',
                                fontWeight: 600,
                                fontSize: '11px',
                            }}
                        />
                    )}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    {/* Practice filter — only rendered when data spans 2+
                      * practices (All Practices tracker with a multi-practice
                      * selection). Defaults to empty = "All selected
                      * practices". Mirrors the Treatment filter's Autocomplete
                      * pattern below. */}
                    {showPracticeColumn && (
                        <Autocomplete
                            multiple
                            size="small"
                            options={effectivePracticeOptions.map((p) => p.id)}
                            getOptionLabel={(id) => effectivePracticeOptions.find((p) => p.id === id)?.name || id}
                            value={selectedPractices}
                            onChange={(_, newValue) => setSelectedPractices(newValue)}
                            disableCloseOnSelect
                            renderOption={(props, option, { selected }) => (
                                <li {...props}>
                                    <Checkbox
                                        size="small"
                                        checked={selected}
                                        sx={{ mr: 1, p: 0 }}
                                    />
                                    <Typography sx={{ fontSize: '13px' }}>
                                        {effectivePracticeOptions.find((p) => p.id === option)?.name || option}
                                    </Typography>
                                </li>
                            )}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    placeholder={selectedPractices.length === 0 ? 'All selected practices' : ''}
                                    sx={{ minWidth: 200 }}
                                />
                            )}
                            renderTags={(value, getTagProps) =>
                                value.length <= 2
                                    ? value.map((option, index) => (
                                        <Chip
                                            {...getTagProps({ index })}
                                            key={option}
                                            label={effectivePracticeOptions.find((p) => p.id === option)?.name || option}
                                            size="small"
                                            sx={{ fontSize: '11px', height: 22 }}
                                        />
                                    ))
                                    : <Typography sx={{ fontSize: '13px', color: '#64748b', ml: 1 }}>
                                        {value.length} practices
                                    </Typography>
                            }
                            sx={{ minWidth: 220 }}
                        />
                    )}
                    {/* Treatment filter */}
                    {allTreatmentNames.length > 0 && (
                        <Autocomplete
                            multiple
                            size="small"
                            options={allTreatmentNames}
                            value={selectedTreatments}
                            onChange={handleTreatmentChange}
                            disableCloseOnSelect
                            renderOption={(props, option, { selected }) => (
                                <li {...props}>
                                    <Checkbox
                                        size="small"
                                        checked={selected}
                                        sx={{ mr: 1, p: 0 }}
                                    />
                                    <Typography sx={{ fontSize: '13px' }}>{option}</Typography>
                                </li>
                            )}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    placeholder={selectedTreatments.length === 0 ? 'Select treatments...' : ''}
                                    sx={{ minWidth: 200 }}
                                />
                            )}
                            renderTags={(value, getTagProps) =>
                                value.length <= 2
                                    ? value.map((option, index) => (
                                        <Chip
                                            {...getTagProps({ index })}
                                            key={option}
                                            label={option}
                                            size="small"
                                            sx={{ fontSize: '11px', height: 22 }}
                                        />
                                    ))
                                    : <Typography sx={{ fontSize: '13px', color: '#64748b', ml: 1 }}>
                                        {value.length} treatments
                                    </Typography>
                            }
                            sx={{ minWidth: 220 }}
                        />
                    )}
                    {/* Dentist search */}
                    <TextField
                        size="small"
                        placeholder="Search team member..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        sx={{ width: 180 }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Iconify icon="eva:search-outline" width={20} sx={{ color: 'text.disabled' }} />
                                </InputAdornment>
                            ),
                        }}
                    />

                    {/* Divider between search and toolbar icons */}
                    <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

                    {/* Column & row visibility */}
                    <Tooltip title="Show/hide columns & rows" arrow>
                        <Badge
                            variant="dot"
                            invisible={!isFilterActive}
                            sx={{
                                '& .MuiBadge-badge': {
                                    bgcolor: '#3b82f6',
                                    width: 8,
                                    height: 8,
                                    minWidth: 8,
                                    top: 4,
                                    right: 4,
                                },
                            }}
                        >
                            <IconButton
                                size="small"
                                onClick={(e) => setColumnsAnchor(e.currentTarget)}
                                sx={{
                                    width: 32,
                                    height: 32,
                                    border: '1px solid',
                                    borderColor: (isFilterActive || columnsAnchor) ? 'primary.main' : '#e2e8f0',
                                    borderRadius: '8px',
                                    bgcolor: columnsAnchor ? alpha('#3b82f6', 0.08) : 'transparent',
                                    '&:hover': { bgcolor: alpha('#3b82f6', 0.08) },
                                }}
                            >
                                <Iconify icon="eva:options-2-outline" width={16} sx={{ color: (isFilterActive || columnsAnchor) ? 'primary.main' : '#64748b' }} />
                            </IconButton>
                        </Badge>
                    </Tooltip>

                    {/* Export */}
                    <Tooltip title="Export data" arrow>
                        <IconButton
                            size="small"
                            onClick={(e) => setExportAnchor(e.currentTarget)}
                            sx={{
                                width: 32,
                                height: 32,
                                border: '1px solid',
                                borderColor: exportAnchor ? 'primary.main' : '#e2e8f0',
                                borderRadius: '8px',
                                bgcolor: exportAnchor ? alpha('#3b82f6', 0.08) : 'transparent',
                                '&:hover': { bgcolor: alpha('#3b82f6', 0.08) },
                            }}
                        >
                            <Iconify icon="eva:download-outline" width={16} sx={{ color: exportAnchor ? 'primary.main' : '#64748b' }} />
                        </IconButton>
                    </Tooltip>

                    {/* Schedule email report */}
                    <Tooltip title="Schedule this report via email" arrow>
                        <IconButton
                            size="small"
                            onClick={() => navigate(`${PATH_DASHBOARD.analyticsReports.settings}?prefill=PRACTICE_USER_LEAD_CONVERSIONS`)}
                            sx={{
                                width: 32,
                                height: 32,
                                border: '1px solid',
                                borderColor: '#e2e8f0',
                                borderRadius: '8px',
                                '&:hover': { bgcolor: alpha('#7c3aed', 0.08), borderColor: '#7c3aed' },
                            }}
                        >
                            <Iconify icon="mdi:email-fast-outline" width={16} sx={{ color: '#64748b' }} />
                        </IconButton>
                    </Tooltip>

                    {/* Column visibility popover */}
                    <Popover
                        open={Boolean(columnsAnchor)}
                        anchorEl={columnsAnchor}
                        onClose={() => setColumnsAnchor(null)}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                        slotProps={{ paper: { sx: { mt: 1, borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.12)', width: 280, maxHeight: 520 } } }}
                    >
                        <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #f1f5f9' }}>
                            <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#1e293b' }}>
                                Treatment Columns
                            </Typography>
                        </Box>
                        <List dense sx={{ py: 0.5, maxHeight: 220, overflow: 'auto' }}>
                            {/* Selected treatments first — in display order with reorder controls */}
                            {selectedTreatments.map((name, idx) => (
                                <ListItemButton
                                    key={name}
                                    sx={{ py: 0.25, px: 1.5, gap: 0 }}
                                    disableRipple
                                >
                                    <Checkbox
                                        size="small"
                                        checked
                                        onClick={() => {
                                            const updated = selectedTreatments.filter((t) => t !== name);
                                            setSelectedTreatments(updated);
                                            savePreferences(updated, selectedMetrics);
                                        }}
                                        sx={{ p: 0, mr: 1 }}
                                    />
                                    <ListItemText
                                        primary={name}
                                        primaryTypographyProps={{ fontSize: '13px', color: '#1e293b', noWrap: true }}
                                        sx={{ flex: 1, minWidth: 0 }}
                                    />
                                    <Box sx={{ display: 'flex', ml: 0.5, flexShrink: 0 }}>
                                        <IconButton
                                            size="small"
                                            disabled={idx === 0}
                                            onClick={(e) => { e.stopPropagation(); handleMoveTreatment(name, 'up'); }}
                                            sx={{ p: 0.25 }}
                                        >
                                            <Iconify icon="mdi:chevron-up" width={16} sx={{ color: idx === 0 ? '#d1d5db' : '#64748b' }} />
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            disabled={idx === selectedTreatments.length - 1}
                                            onClick={(e) => { e.stopPropagation(); handleMoveTreatment(name, 'down'); }}
                                            sx={{ p: 0.25 }}
                                        >
                                            <Iconify icon="mdi:chevron-down" width={16} sx={{ color: idx === selectedTreatments.length - 1 ? '#d1d5db' : '#64748b' }} />
                                        </IconButton>
                                    </Box>
                                </ListItemButton>
                            ))}
                            {/* Unselected treatments */}
                            {allTreatmentNames.filter((n) => !selectedTreatments.includes(n)).map((name) => (
                                <ListItemButton
                                    key={name}
                                    onClick={() => {
                                        const updated = [...selectedTreatments, name];
                                        setSelectedTreatments(updated);
                                        savePreferences(updated, selectedMetrics);
                                    }}
                                    sx={{ py: 0.25, px: 1.5, opacity: 0.6 }}
                                >
                                    <Checkbox
                                        size="small"
                                        checked={false}
                                        sx={{ p: 0, mr: 1 }}
                                    />
                                    <ListItemText
                                        primary={name}
                                        primaryTypographyProps={{ fontSize: '13px', color: '#94a3b8', noWrap: true }}
                                    />
                                </ListItemButton>
                            ))}
                        </List>
                        <Box sx={{ px: 2, py: 1.5, borderTop: '1px solid #f1f5f9', borderBottom: '1px solid #f1f5f9' }}>
                            <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#1e293b' }}>
                                Metric Rows
                            </Typography>
                        </Box>
                        <List dense sx={{ py: 0.5, maxHeight: 220, overflow: 'auto' }}>
                            {/* Selected metrics in display order with reorder controls */}
                            {selectedMetrics.map((key, idx) => {
                                const metric = ROW_METRICS.find((m) => m.key === key);
                                if (!metric) return null;
                                return (
                                    <ListItemButton
                                        key={key}
                                        sx={{ py: 0.25, px: 1.5, gap: 0 }}
                                        disableRipple
                                    >
                                        <Checkbox
                                            size="small"
                                            checked
                                            onClick={() => handleMetricToggle(key)}
                                            sx={{ p: 0, mr: 1 }}
                                        />
                                        <Box sx={{ width: 4, height: 14, borderRadius: 1, bgcolor: metric.color, mr: 1, flexShrink: 0 }} />
                                        <ListItemText
                                            primary={metric.label}
                                            primaryTypographyProps={{ fontSize: '13px', color: '#1e293b' }}
                                            sx={{ flex: 1, minWidth: 0 }}
                                        />
                                        <Box sx={{ display: 'flex', ml: 0.5, flexShrink: 0 }}>
                                            <IconButton
                                                size="small"
                                                disabled={idx === 0}
                                                onClick={(e) => { e.stopPropagation(); handleMoveMetric(key, 'up'); }}
                                                sx={{ p: 0.25 }}
                                            >
                                                <Iconify icon="mdi:chevron-up" width={16} sx={{ color: idx === 0 ? '#d1d5db' : '#64748b' }} />
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                disabled={idx === selectedMetrics.length - 1}
                                                onClick={(e) => { e.stopPropagation(); handleMoveMetric(key, 'down'); }}
                                                sx={{ p: 0.25 }}
                                            >
                                                <Iconify icon="mdi:chevron-down" width={16} sx={{ color: idx === selectedMetrics.length - 1 ? '#d1d5db' : '#64748b' }} />
                                            </IconButton>
                                        </Box>
                                    </ListItemButton>
                                );
                            })}
                            {/* Unselected metrics */}
                            {ROW_METRICS.filter((m) => !selectedMetrics.includes(m.key)).map((metric) => (
                                <ListItemButton
                                    key={metric.key}
                                    onClick={() => handleMetricToggle(metric.key)}
                                    sx={{ py: 0.25, px: 1.5, opacity: 0.6 }}
                                >
                                    <Checkbox
                                        size="small"
                                        checked={false}
                                        sx={{ p: 0, mr: 1 }}
                                    />
                                    <Box sx={{ width: 4, height: 14, borderRadius: 1, bgcolor: metric.color, mr: 1, flexShrink: 0, opacity: 0.4 }} />
                                    <ListItemText
                                        primary={metric.label}
                                        primaryTypographyProps={{ fontSize: '13px', color: '#94a3b8' }}
                                    />
                                </ListItemButton>
                            ))}
                        </List>
                    </Popover>

                    {/* Export popover */}
                    <Popover
                        open={Boolean(exportAnchor)}
                        anchorEl={exportAnchor}
                        onClose={() => setExportAnchor(null)}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                        slotProps={{ paper: { sx: { mt: 1, borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.12)', minWidth: 200 } } }}
                    >
                        <List dense sx={{ py: 0.5 }}>
                            <ListItemButton onClick={handleExportCSV} sx={{ py: 1, px: 2 }}>
                                <Iconify icon="mdi:file-delimited-outline" width={18} sx={{ color: '#64748b', mr: 1.5 }} />
                                <ListItemText
                                    primary="Export Summary CSV"
                                    primaryTypographyProps={{ fontSize: '13px' }}
                                />
                            </ListItemButton>
                            <ListItemButton onClick={handleExportDetailCSV} sx={{ py: 1, px: 2 }}>
                                <Iconify icon="mdi:table-large" width={18} sx={{ color: '#64748b', mr: 1.5 }} />
                                <ListItemText
                                    primary="Export Detailed CSV"
                                    primaryTypographyProps={{ fontSize: '13px' }}
                                />
                            </ListItemButton>
                        </List>
                    </Popover>
                </Box>
            </Box>

            {/* Partial-coverage banner — visible whenever one or more selected
              * practices failed to load (access denied / hard error). Keeps
              * the summary/export honest instead of silently showing a
              * union that omits data for practices that didn't respond. */}
            {isPartialCoverage && (
                <Box
                    sx={{
                        px: 2.5,
                        py: 1.25,
                        bgcolor: alpha('#f59e0b', 0.08),
                        borderBottom: '1px solid',
                        borderColor: alpha('#f59e0b', 0.24),
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                    }}
                >
                    <Iconify icon="mdi:alert-outline" width={16} sx={{ color: '#b45309', flexShrink: 0 }} />
                    <Typography sx={{ fontSize: '12.5px', color: '#92400e' }}>
                        {`Showing ${coveredCount} of ${selectedCount} selected practices. Couldn't load: `}
                        {failedPractices.map((fp, idx) => (
                            // eslint-disable-next-line react/no-array-index-key
                            <Box component="span" key={`${fp.practiceId}-${idx}`}>
                                {idx > 0 ? ', ' : ''}
                                {`${fp.practiceName} (${fp.reason})`}
                            </Box>
                        ))}
                    </Typography>
                </Box>
            )}

            <DataScopeIndicator variant="practitioner" />

            {/* Scrollable table container */}
            <Box sx={{ overflowX: 'auto' }}>
                <Box sx={{ minWidth: 900 }}>
                    {/* Column Headers - matching DataGrid style */}
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: gridTemplate,
                            bgcolor: '#f8fafc',
                            borderBottom: '1px solid #e2e8f0',
                        }}
                    >
                        {/* Team Member header — sortable */}
                        <Box
                            onClick={() => handleSort('name')}
                            sx={{
                                p: 1.5,
                                pl: 2,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                cursor: 'pointer',
                                userSelect: 'none',
                                '&:hover .sort-icon': { opacity: 1 },
                            }}
                        >
                            <Typography
                                sx={{
                                    fontWeight: 600,
                                    fontSize: '12px',
                                    color: sortField === 'name' ? '#1e293b' : '#64748b',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px',
                                }}
                            >
                                Team Member
                            </Typography>
                            <Iconify
                                className="sort-icon"
                                icon={sortField === 'name' && sortDirection === 'asc' ? 'mdi:arrow-up' : 'mdi:arrow-down'}
                                width={14}
                                sx={{ color: sortField === 'name' ? '#1e293b' : '#94a3b8', opacity: sortField === 'name' ? 1 : 0, transition: 'opacity 0.15s' }}
                            />
                        </Box>
                        {/* Practice header — present only when data spans 2+ practices */}
                        {showPracticeColumn && (
                            <Box sx={{ p: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <Typography
                                    sx={{
                                        fontWeight: 600,
                                        fontSize: '12px',
                                        color: '#64748b',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px',
                                    }}
                                >
                                    Practice
                                </Typography>
                            </Box>
                        )}
                        {/* Treatment headers — sortable */}
                        {treatmentColumns.map((treatment) => {
                            const isActive = sortField === treatment;
                            return (
                                <Box
                                    key={treatment}
                                    onClick={() => handleSort(treatment)}
                                    sx={{
                                        p: 1.5,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: 0.5,
                                        cursor: 'pointer',
                                        userSelect: 'none',
                                        '&:hover .sort-icon': { opacity: 1 },
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontWeight: 600,
                                            fontSize: '11px',
                                            color: isActive ? '#1e293b' : '#64748b',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px',
                                            textAlign: 'center',
                                        }}
                                    >
                                        {treatment}
                                    </Typography>
                                    <Iconify
                                        className="sort-icon"
                                        icon={isActive && sortDirection === 'asc' ? 'mdi:arrow-up' : 'mdi:arrow-down'}
                                        width={14}
                                        sx={{ color: isActive ? '#1e293b' : '#94a3b8', opacity: isActive ? 1 : 0, transition: 'opacity 0.15s', flexShrink: 0 }}
                                    />
                                </Box>
                            );
                        })}
                        {/* Total header — sortable */}
                        <Box
                            onClick={() => handleSort('total')}
                            sx={{
                                p: 1.5,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 0.5,
                                cursor: 'pointer',
                                userSelect: 'none',
                                '&:hover .sort-icon': { opacity: 1 },
                            }}
                        >
                            <Typography
                                sx={{
                                    fontWeight: 600,
                                    fontSize: '12px',
                                    color: sortField === 'total' ? '#1e293b' : '#64748b',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px',
                                }}
                            >
                                Total
                            </Typography>
                            <Iconify
                                className="sort-icon"
                                icon={sortField === 'total' && sortDirection === 'asc' ? 'mdi:arrow-up' : 'mdi:arrow-down'}
                                width={14}
                                sx={{ color: sortField === 'total' ? '#1e293b' : '#94a3b8', opacity: sortField === 'total' ? 1 : 0, transition: 'opacity 0.15s' }}
                            />
                        </Box>
                    </Box>

                    {/* Practitioners */}
                    {filteredPractitioners.map((practitioner, pIndex) => {
                        const rowKey = getRowKey(practitioner);
                        const isExpanded = expandedPractitioner === rowKey;

                        return (
                            <Box
                                key={rowKey}
                                sx={{
                                    borderBottom: '1px solid #f1f5f9',
                                    '&:last-child': { borderBottom: 'none' },
                                }}
                            >
                                {/* Practitioner Name Row - Collapsed view */}
                                <Box
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: gridTemplate,
                                        bgcolor: pIndex % 2 === 0 ? '#fff' : '#fafbfc',
                                        cursor: 'pointer',
                                        '&:hover': { bgcolor: alpha('#3b82f6', 0.04) },
                                    }}
                                    onClick={() => handleToggleExpand(rowKey)}
                                >
                                    {/* Practitioner name with avatar */}
                                    <Box
                                        sx={{
                                            p: 1.5,
                                            pl: 2,
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                        }}
                                    >
                                        <IconButton
                                            size="small"
                                            sx={{ p: 0.5 }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleToggleExpand(rowKey);
                                            }}
                                        >
                                            <Iconify
                                                icon={isExpanded ? 'mdi:chevron-up' : 'mdi:chevron-down'}
                                                width={18}
                                                sx={{ color: '#64748b' }}
                                            />
                                        </IconButton>
                                        <DemoBlur>
                                        <Avatar
                                            src={practitioner.avatar}
                                            sx={{
                                                width: 32,
                                                height: 32,
                                                bgcolor: practitioner._isUnassigned ? '#f1f5f9' : '#e2e8f0',
                                                fontSize: '12px',
                                                fontWeight: 600,
                                                color: '#64748b',
                                            }}
                                        >
                                            {practitioner._isUnassigned
                                                ? <Iconify icon="mdi:account-question-outline" width={20} />
                                                : practitioner.name?.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase()}
                                        </Avatar>
                                        </DemoBlur>
                                        <Typography sx={{ fontWeight: 600, fontSize: '14px', color: practitioner._isUnassigned ? '#94a3b8' : '#1e293b', fontStyle: practitioner._isUnassigned ? 'italic' : 'normal' }}>
                                            <DemoBlur>{practitioner.name}</DemoBlur>
                                        </Typography>
                                    </Box>

                                    {/* Practice cell — present only when data spans 2+ practices */}
                                    {showPracticeColumn && (
                                        <Box sx={{ p: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            <Chip
                                                label={practitioner.practiceName || '—'}
                                                size="small"
                                                sx={{
                                                    fontSize: '10px',
                                                    height: 20,
                                                    bgcolor: '#f1f5f9',
                                                    color: '#475569',
                                                    fontWeight: 500,
                                                    maxWidth: '100%',
                                                }}
                                            />
                                        </Box>
                                    )}

                                    {/* Quick summary for each treatment */}
                                    {treatmentColumns.map((treatment) => {
                                        const treatmentData = practitioner.treatments?.[treatment] || null;
                                        const hasData = treatmentData && treatmentData.consultationsBooked > 0;
                                        const conversion = treatmentData?.conversionRate || '0';

                                        return (
                                            <Box
                                                key={treatment}
                                                sx={{
                                                    p: 1.5,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                }}
                                            >
                                                {hasData ? (
                                                    <Chip
                                                        label={`${treatmentData.treatmentStarted}/${treatmentData.consultationsBooked}`}
                                                        size="small"
                                                        onClick={(e) => handleMetricClick(
                                                            e,
                                                            treatmentData.consultationsBookedDetails,
                                                            buildMetricTitle(practitioner, `${treatment} (Consult Booked)`)
                                                        )}
                                                        sx={{
                                                            bgcolor: alpha(getConversionColor(conversion), 0.12),
                                                            color: getConversionColor(conversion),
                                                            fontWeight: 600,
                                                            fontSize: '11px',
                                                            height: 24,
                                                            minWidth: 50,
                                                            cursor: 'pointer',
                                                            '&:hover': {
                                                                bgcolor: alpha(getConversionColor(conversion), 0.24),
                                                            },
                                                        }}
                                                    />
                                                ) : (
                                                    <Typography sx={{ fontSize: '12px', color: 'text.disabled' }}>
                                                        -
                                                    </Typography>
                                                )}
                                            </Box>
                                        );
                                    })}

                                    {/* Total summary */}
                                    <Box
                                        sx={{
                                            p: 1.5,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        }}
                                    >
                                        <Chip
                                            label={`${practitioner.totals?.treatmentStarted || 0}/${practitioner.totals?.consultationsBooked || 0}`}
                                            size="small"
                                            onClick={(e) => handleMetricClick(
                                                e,
                                                practitioner.totals?.consultationsBookedDetails,
                                                buildMetricTitle(practitioner, 'All Treatments (Consult Booked)')
                                            )}
                                            sx={{
                                                bgcolor: alpha(getConversionColor(practitioner.totals?.conversionRate), 0.12),
                                                color: getConversionColor(practitioner.totals?.conversionRate),
                                                fontWeight: 600,
                                                fontSize: '11px',
                                                height: 24,
                                                minWidth: 50,
                                                cursor: 'pointer',
                                                '&:hover': {
                                                    bgcolor: alpha(getConversionColor(practitioner.totals?.conversionRate), 0.24),
                                                },
                                            }}
                                        />
                                    </Box>
                                </Box>

                                {/* Expanded Metric Rows */}
                                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                    {visibleMetrics.map((metric) => (
                                        <Box
                                            key={metric.key}
                                            sx={{
                                                display: 'grid',
                                                gridTemplateColumns: gridTemplate,
                                                bgcolor: alpha(metric.color, 0.03),
                                                borderTop: '1px solid #f1f5f9',
                                            }}
                                        >
                                            {/* Metric label */}
                                            <Box
                                                sx={{
                                                    p: 1,
                                                    pl: 5,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        width: 4,
                                                        height: 20,
                                                        borderRadius: 1,
                                                        bgcolor: metric.color,
                                                        mr: 1.5,
                                                    }}
                                                />
                                                <Typography
                                                    sx={{
                                                        fontWeight: 500,
                                                        fontSize: '12px',
                                                        color: '#64748b',
                                                    }}
                                                >
                                                    {metric.label}
                                                </Typography>
                                            </Box>

                                            {/* Practice filler cell — keeps grid-column count aligned
                                              * with the header/collapsed rows when the Practice column
                                              * is present; this row has no per-practice split to show. */}
                                            {showPracticeColumn && <Box />}

                                            {/* Treatment values */}
                                            {treatmentColumns.map((treatment) => {
                                                const treatmentData = practitioner.treatments?.[treatment] || null;
                                                const value = treatmentData?.[metric.key] || 0;
                                                const hasData = treatmentData && treatmentData.consultationsBooked > 0;
                                                const isConversion = metric.key === 'conversionRate';
                                                const cellColor = isConversion ? getConversionColor(value) : metric.color;
                                                // Get lead IDs for clickable metrics
                                                const leadIdsKey = `${metric.key}Details`;
                                                const leadIds = treatmentData?.[leadIdsKey] || [];
                                                const isClickable = ['consultationsBooked', 'consultationsAttended', 'treatmentStarted'].includes(metric.key) && leadIds.length > 0;

                                                return (
                                                    <Box
                                                        key={`${treatment}-${metric.key}`}
                                                        sx={{
                                                            p: 1,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                        }}
                                                    >
                                                        {hasData && (value > 0 || metric.key === 'conversionRate') ? (
                                                            <Chip
                                                                label={formatValue(
                                                                    value,
                                                                    metric.format,
                                                                    metric.prefix || '',
                                                                    metric.suffix || ''
                                                                )}
                                                                size="small"
                                                                onClick={isClickable ? (e) => handleMetricClick(
                                                                    e,
                                                                    leadIds,
                                                                    buildMetricTitle(practitioner, `${treatment} (${metric.label})`)
                                                                ) : undefined}
                                                                sx={{
                                                                    bgcolor: alpha(cellColor, 0.12),
                                                                    color: cellColor,
                                                                    fontWeight: 600,
                                                                    fontSize: '11px',
                                                                    height: 24,
                                                                    minWidth: 40,
                                                                    cursor: isClickable ? 'pointer' : 'default',
                                                                    '&:hover': isClickable ? {
                                                                        bgcolor: alpha(cellColor, 0.24),
                                                                    } : {},
                                                                }}
                                                            />
                                                        ) : (
                                                            <Typography sx={{ fontSize: '11px', color: 'text.disabled' }}>
                                                                -
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                );
                                            })}

                                            {/* Total column */}
                                            <Box
                                                sx={{
                                                    p: 1,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                }}
                                            >
                                                {(() => {
                                                    const value = practitioner.totals?.[metric.key] || 0;
                                                    const isConversion = metric.key === 'conversionRate';
                                                    const cellColor = isConversion ? getConversionColor(value) : metric.color;
                                                    // Get lead IDs for clickable metrics
                                                    const leadIdsKey = `${metric.key}Details`;
                                                    const leadIds = practitioner.totals?.[leadIdsKey] || [];
                                                    const isClickable = ['consultationsBooked', 'consultationsAttended', 'treatmentStarted'].includes(metric.key) && leadIds.length > 0;
                                                    return (
                                                        <Chip
                                                            label={formatValue(
                                                                value,
                                                                metric.format,
                                                                metric.prefix || '',
                                                                metric.suffix || ''
                                                            )}
                                                            size="small"
                                                            onClick={isClickable ? (e) => handleMetricClick(
                                                                e,
                                                                leadIds,
                                                                buildMetricTitle(practitioner, `Total (${metric.label})`)
                                                            ) : undefined}
                                                            sx={{
                                                                bgcolor: alpha(cellColor, 0.12),
                                                                color: cellColor,
                                                                fontWeight: 600,
                                                                fontSize: '11px',
                                                                height: 24,
                                                                minWidth: 40,
                                                                cursor: isClickable ? 'pointer' : 'default',
                                                                '&:hover': isClickable ? {
                                                                    bgcolor: alpha(cellColor, 0.24),
                                                                } : {},
                                                            }}
                                                        />
                                                    );
                                                })()}
                                            </Box>
                                        </Box>
                                    ))}
                                </Collapse>
                            </Box>
                        );
                    })}

                    {filteredPractitioners.length === 0 && (
                        <Box sx={{ textAlign: 'center', py: 6 }}>
                            <Typography sx={{ color: '#64748b' }}>
                                No team members found
                            </Typography>
                        </Box>
                    )}
                </Box>
            </Box>

            {/* Footer legend */}
            <Box
                sx={{
                    p: 2,
                    bgcolor: '#f8fafc',
                    borderTop: '1px solid #e2e8f0',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 2,
                }}
            >
                <Box sx={{ display: 'flex', gap: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#10b981' }} />
                        <Typography sx={{ fontSize: '11px', color: '#64748b' }}>≥70% conversion</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#f59e0b' }} />
                        <Typography sx={{ fontSize: '11px', color: '#64748b' }}>50-69% conversion</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#ef4444' }} />
                        <Typography sx={{ fontSize: '11px', color: '#64748b' }}>&lt;50% conversion</Typography>
                    </Box>
                </Box>
                <Typography sx={{ fontSize: '11px', color: '#94a3b8' }}>
                    Click row to expand • Click numbers to view leads
                </Typography>
            </Box>

            {/* Lead Treatment Details Popup */}
            <Dialog
                open={popupOpen}
                onClose={handleClosePopup}
                maxWidth="xl"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: '16px',
                        height: 'auto',
                        maxHeight: '90vh',
                    },
                }}
            >
                <Box sx={{ p: 0 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            p: 2.5,
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                            background: `linear-gradient(135deg, ${alpha('#3b82f6', 0.08)} 0%, ${alpha('#3b82f6', 0.02)} 100%)`,
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Box
                                sx={{
                                    width: 44,
                                    height: 44,
                                    borderRadius: '10px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    bgcolor: alpha('#8b5cf6', 0.1),
                                }}
                            >
                                <Iconify
                                    icon="mdi:account-details"
                                    width={22}
                                    height={22}
                                    sx={{ color: '#8b5cf6' }}
                                />
                            </Box>
                            <Box>
                                <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '18px' }}>
                                    {popupTitle} ({popupDetails.length})
                                </Typography>
                                {!!exportPracticeName && (
                                    <Typography
                                        variant="body2"
                                        data-testid="drilldown-practice-scope"
                                        sx={{ color: '#64748b', mt: 0.25 }}
                                    >
                                        {exportPracticeName}
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Tooltip title="Export CSV" arrow>
                                <IconButton
                                    size="small"
                                    aria-label="Export CSV"
                                    onClick={handleExportPopupCSV}
                                    disabled={!popupDetails?.length}
                                    sx={{
                                        width: 36,
                                        height: 36,
                                        border: '1px solid',
                                        borderColor: '#e2e8f0',
                                        borderRadius: '8px',
                                        '&:hover': { bgcolor: alpha('#3b82f6', 0.08) },
                                    }}
                                >
                                    <Iconify icon="eva:download-outline" width={18} sx={{ color: '#64748b' }} />
                                </IconButton>
                            </Tooltip>
                            <IconButton onClick={handleClosePopup} size="small">
                                <Iconify icon="eva:close-fill" width={24} />
                            </IconButton>
                        </Box>
                    </Box>
                    {/* Headers/cells use whiteSpace: nowrap so dynamic labels
                      * (Date column in particular) never wrap to a second line. */}
                    <Box sx={{ minHeight: 520, maxHeight: 640, overflow: 'auto' }}>
                        <Table size="medium" stickyHeader sx={{ minWidth: 1020 }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 600, fontSize: '12px', whiteSpace: 'nowrap', width: 48 }}>#</TableCell>
                                    <TableCell sx={{ fontWeight: 600, fontSize: '12px', whiteSpace: 'nowrap' }}>Lead Name</TableCell>
                                    <TableCell sx={{ fontWeight: 600, fontSize: '12px', whiteSpace: 'nowrap' }}>Treatment</TableCell>
                                    <TableCell sx={{ fontWeight: 600, fontSize: '12px', whiteSpace: 'nowrap' }}>Status</TableCell>
                                    <TableCell sx={{ fontWeight: 600, fontSize: '12px', whiteSpace: 'nowrap' }} align="center">Times contacted</TableCell>
                                    <TableCell sx={{ fontWeight: 600, fontSize: '12px', whiteSpace: 'nowrap' }}>Date</TableCell>
                                    <TableCell sx={{ fontWeight: 600, fontSize: '12px', whiteSpace: 'nowrap' }} align="right">Price</TableCell>
                                    <TableCell sx={{ fontWeight: 600, fontSize: '12px', whiteSpace: 'nowrap' }} align="right">Teeth</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {popupDetails.map((row, idx) => {
                                    // Format date from "DD-MM-YYYY HH:mm:ss" or ISO format
                                    let dateStr = '-';
                                    if (row.createdAt) {
                                        try {
                                            const parts = row.createdAt.match(/^(\d{2})-(\d{2})-(\d{4})/);
                                            if (parts) {
                                                dateStr = `${parts[1]}/${parts[2]}/${parts[3]}`;
                                            } else {
                                                const d = new Date(row.createdAt);
                                                if (!Number.isNaN(d.getTime())) {
                                                    dateStr = d.toLocaleDateString('en-GB');
                                                }
                                            }
                                        } catch { dateStr = '-'; }
                                    }
                                    return (
                                        <TableRow key={`${row.leadId}-${row.treatment}-${idx}`} hover>
                                            <TableCell sx={{ fontSize: '12px', whiteSpace: 'nowrap' }}>{idx + 1}</TableCell>
                                            <TableCell sx={{ fontSize: '12px', fontWeight: 500, whiteSpace: 'nowrap' }}>
                                                {row.practiceId && row.leadId ? (
                                                    <Link
                                                        href={`/dashboard/practice/${row.practiceId}/lead-detail/${row.leadId}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        underline="hover"
                                                        sx={{ color: 'primary.main', fontWeight: 500 }}
                                                    >
                                                        {row.leadName}
                                                    </Link>
                                                ) : row.leadName}
                                            </TableCell>
                                            <TableCell
                                                sx={{
                                                    fontSize: '12px',
                                                    whiteSpace: 'nowrap',
                                                    maxWidth: 240,
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                }}
                                                title={row.treatment || ''}
                                            >
                                                {row.treatment}
                                            </TableCell>
                                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                                <Chip
                                                    label={statusLabelOf(row.status)}
                                                    size="small"
                                                    sx={{
                                                        fontSize: '10px',
                                                        height: 20,
                                                        bgcolor: row.status === 'TREATMENT_STARTED' || row.status === 'CLOSED'
                                                            ? alpha('#10b981', 0.12) : alpha('#3b82f6', 0.12),
                                                        color: row.status === 'TREATMENT_STARTED' || row.status === 'CLOSED'
                                                            ? '#10b981' : '#3b82f6',
                                                        fontWeight: 600,
                                                        whiteSpace: 'nowrap',
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell sx={{ fontSize: '12px', whiteSpace: 'nowrap' }} align="center">
                                                {row.timesContacted || 0}
                                            </TableCell>
                                            <TableCell sx={{ fontSize: '12px', whiteSpace: 'nowrap' }}>{dateStr}</TableCell>
                                            <TableCell sx={{ fontSize: '12px', whiteSpace: 'nowrap' }} align="right">
                                                {row.price > 0 ? `£${row.price.toLocaleString()}` : '-'}
                                            </TableCell>
                                            <TableCell sx={{ fontSize: '12px', whiteSpace: 'nowrap' }} align="right">
                                                {row.teethCount > 0 ? row.teethCount : '-'}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                                {popupDetails.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={8} sx={{ textAlign: 'center', py: 4, color: '#94a3b8' }}>
                                            No data
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </Box>
                </Box>
            </Dialog>
        </Box>
    );
}

ClinicianConversionTracker.propTypes = {
    title: PropTypes.string,
    data: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            name: PropTypes.string,
            avatar: PropTypes.string,
            treatments: PropTypes.object,
            totals: PropTypes.object,
        })
    ),
    treatments: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            name: PropTypes.string,
        })
    ),
    teamTotals: PropTypes.object,
    isLoading: PropTypes.bool,
    // Optional overrides for CSV export filenames — used by the All Practices
    // page's clinician section so exports carry that page's date range and the
    // ONE practice the tracker is scoped to, instead of the current-practice /
    // header date range useReportExportFilename falls back to by default.
    // Left undefined (the default) leaves By Practitioner's export filenames
    // byte-identical to before this prop existed.
    exportDateRange: PropTypes.shape({
        startDate: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
        endDate: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
    }),
    exportPracticeName: PropTypes.string,
    // The FULL page-selected practice set — authoritative source for the
    // Practice column/filter (see `showPracticeColumn`/`effectivePracticeOptions`
    // above), so a practice that returned zero rows or failed to load doesn't
    // silently disappear from the column/filter. Left empty (the default —
    // By Practitioner / single-practice usage), this is fully inert.
    practiceScope: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
    })),
    // Practices from `practiceScope` that failed to contribute data (access
    // denied or a hard fetch error) — NOT populated for a practice that is
    // simply, honestly empty. Drives the partial-coverage banner + marks the
    // summary totals/CSV exports as partial.
    failedPractices: PropTypes.arrayOf(PropTypes.shape({
        practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        practiceName: PropTypes.string,
        reason: PropTypes.oneOf(['access', 'error']),
    })),
    selectedCount: PropTypes.number,
    coveredCount: PropTypes.number,
    // Retry handler for the zero-row partial/access card above — set by the
    // All Practices page's clinician section (its existing Retry action).
    // Left undefined (the default — By Practitioner / single-practice usage),
    // that card never renders a Retry button.
    onRetry: PropTypes.func,
};

ClinicianConversionTracker.defaultProps = {
    title: 'Clinician Conversion Tracker',
    data: [],
    treatments: [],
    teamTotals: {},
    isLoading: false,
    exportDateRange: undefined,
    exportPracticeName: undefined,
    practiceScope: [],
    failedPractices: [],
    selectedCount: 0,
    coveredCount: 0,
    onRetry: undefined,
};
